import React, { useRef } from 'react';
import { Box, Button, Flex } from 'rebass';
import { FiCamera, FiTrash2 } from 'react-icons/fi';
import { Input, Label } from '@rebass/forms';

import useForm from 'hooks/useForm';
import InputError from 'components/InputError';
import Grid from 'components/Grid';
import { EXTERNAL_RESTRICTIONS_QUILL_MODULES, NO_VIDEO_QUILL_MODULES } from 'utils/constants';
import SaveEditEditor from 'components/SaveEditEditor';
import { Skeleton } from 'components/Skeleton';
import { useToastHelper } from 'hooks/useToastHelper';
import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';
import { useDebounce } from 'hooks/useDebounce';
import services from 'services/services';

/**
 * Check if the provided code is a valid color.
 *
 * @param {string} code - Color code
 * @returns {boolean} Whether the code is a valid color
 */
const isColor = code => {
    const s = new Option().style;
    s.color = code;
    return s.color !== '';
};

/**
 * Color indicator component. Shows a color of the provided hex or text.
 *
 * @param {string} color - Color hex or text
 */
const ColorIndicator = ({ color }) => (
    <div
        style={{
            display: 'inline-block',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: color
        }}
    />
);

/**
 * A hook function to validate the branding form payload.
 *
 * This function validates the fields of the branding form payload to ensure they meet
 * the required criteria.
 *
 * @function
 * @returns {(form: Object) => [boolean, {field: string, message: string}] | [boolean]} function to validate the branding form payload
 */
const useBrandingFormValidator = () => {
    /**
     * Validates whether the provided color input is a valid color value and meets the required criteria.
     *
     * @param {string} color - The color value to validate.
     * @returns {boolean} - Returns true if the color is valid, false otherwise.
     */
    const checkIfColorIsValid = color => {
        // The color must be a valid hex code or text.
        if (!isColor(color)) return false;

        // The color can have up to 7 characters.
        if (color.length > 7) return false;

        return true;
    };

    return form => {
        if (form.primary_color && !checkIfColorIsValid(form.primary_color))
            return [
                false,
                {
                    field: 'primary_color',
                    message: 'Invalid primary color'
                }
            ];

        if (form.secondary_color && !checkIfColorIsValid(form.secondary_color))
            return [
                false,
                {
                    field: 'secondary_color',
                    message: 'Invalid secondary color'
                }
            ];

        if (!form.name?.trim())
            return [
                false,
                {
                    field: 'name',
                    message: 'Branding name is required'
                }
            ];

        return [true];
    };
};

/**
 * Branding form. Used to update branding settings for a study/account/panel.
 *
 * @param {number} brandingId - Branding ID
 * @param {{ custom_domain?: string }} [account] - Account
 */
export function BrandingForm({ brandingId, account }) {
    const { register, watch, reset } = useForm();
    const { addSuccessToast, addErrorToast } = useToastHelper();
    const validateForm = useBrandingFormValidator();
    const { data: branding = {}, refetch, isLoading: isLoadingBranding } = useQuery({
        queryFn: () => services.getBranding(brandingId),
        onSuccess: data =>
            reset({
                name: data.name,
                primary_color: data.primary_color,
                secondary_color: data.secondary_color,
                email_footer: data.email_footer,
                form_footer: data.form_footer
            })
    });
    const { isLoading: isLogoUploading, mutate: uploadLogo } = useMutation({
        mutationFn: file => services.uploadBrandingLogo(brandingId, file),
        onSuccess: refetch
    });
    const { isLoading: isLogoRemoving, mutate: deleteLogo } = useMutation({
        mutationFn: () => services.deleteBrandingLogo(brandingId),
        onSuccess: refetch
    });
    const { isLoading: isBackgroundUploading, mutate: uploadBackground } = useMutation({
        mutationFn: file => services.uploadBrandingBackground(brandingId, file),
        onSuccess: refetch
    });
    const { isLoading: isBackgroundRemoving, mutate: deleteBackground } = useMutation({
        mutationFn: () => services.deleteBrandingBackground(brandingId),
        onSuccess: refetch
    });
    const { mutate: updateBranding } = useMutation({
        mutationFn: payload => services.updateBranding(brandingId, payload),
        onSuccess: () => addSuccessToast('Branding updated successfully'),
        onError: (_, errorText) => addErrorToast(errorText)
    });
    const { isLoading: isFaviconUploading, mutate: uploadFavicon } = useMutation({
        mutationFn: file => services.uploadBrandingFavicon(brandingId, file),
        onSuccess: refetch
    });
    const { isLoading: isFaviconRemoving, mutate: deleteFavicon } = useMutation({
        mutationFn: () => services.deleteBrandingFavicon(brandingId),
        onSuccess: refetch
    });
    /**
     * @type {MutableRefObject<HTMLInputElement>}
     */
    const uploadLogoInputRef = useRef();
    /**
     * @type {MutableRefObject<HTMLInputElement>}
     */
    const uploadBackgroundInputRef = useRef();
    /**
     * @type {MutableRefObject<HTMLInputElement>}
     */
    const uploadFaviconInputRef = useRef();

    const formState = watch();
    const isPrimaryColorValid = formState.primary_color ? isColor(formState.primary_color) : true;
    const isSecondaryColorValid = formState.secondary_color ? isColor(formState.secondary_color) : true;
    const isCustomDomain = account && account.custom_domain;

    const changeBranding = useDebounce((key, value) => {
        const payload = {
            name: branding.name,
            primary_color: branding.primary_color || null,
            secondary_color: branding.secondary_color || null,
            email_footer: branding.email_footer || null,
            form_footer: branding.form_footer || null,
            [key]: value
        };

        const [isValid, error] = validateForm(payload);

        if (!isValid) {
            // In case of name error, we want to show the error toast immediately.
            if (error?.field === 'name') return addErrorToast(error.message);

            // In case of any other error, we just want to no send the request.
            // This is to avoid showing the error toast for every keystroke.
            return;
        }

        return updateBranding(payload);
    }, 500);

    const triggerLogoUpload = () => {
        uploadLogoInputRef.current.click();
    };

    const triggerBackgroundImageUpload = () => {
        uploadBackgroundInputRef.current.click();
    };

    const triggerFaviconUpload = () => {
        if (!isCustomDomain) return;

        uploadFaviconInputRef.current.click();
    };

    if (isLoadingBranding && !branding) return <BrandingFormSkeleton />;

    return (
        <Box style={{ width: '100%' }}>
            <Box>
                <Label htmlFor="branding-name-input">Branding Name (Internal)</Label>
                <Box className="form-data">
                    <Input
                        id="branding-name-input"
                        type="text"
                        {...register('name', {
                            onChange: event => changeBranding('name', event.target.value)
                        })}
                    />
                </Box>
            </Box>
            <Box className="form-row-margin">
                <Label htmlFor="branding-logo-input">Logo</Label>
                <Box className="form-data">
                    {branding.logo && (
                        <img
                            src={branding.logo}
                            style={{
                                width: '64px',
                                height: '64px',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                overflow: 'hidden'
                            }}
                            alt="Branding logo"
                        />
                    )}
                    <Flex style={{ gap: '12px' }}>
                        <Button type="button" variant="link" onClick={triggerLogoUpload} disabled={isLogoUploading}>
                            {isLogoUploading ? (
                                'Uploading...'
                            ) : (
                                <>
                                    <FiCamera /> Upload image
                                </>
                            )}
                        </Button>
                        <input
                            id="branding-logo-input"
                            ref={uploadLogoInputRef}
                            type="file"
                            accept=".png, .jpg, .jpeg"
                            hidden
                            onChange={event => uploadLogo(event.target.files[0])}
                        />
                        {branding.logo && (
                            <Button type="button" variant="link" disabled={isLogoRemoving} onClick={() => deleteLogo()}>
                                <FiTrash2 /> Remove
                            </Button>
                        )}
                    </Flex>
                </Box>
            </Box>
            <Box className="form-row-margin">
                <Label htmlFor="branding-background-input">Screener Background</Label>
                <Box className="form-data">
                    {branding.background_image && (
                        <Box mb={2}>
                            <img
                                src={branding.background_image}
                                style={{
                                    width: '128px',
                                    height: '64px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    overflow: 'hidden'
                                }}
                                alt="Screener Background"
                            />
                        </Box>
                    )}
                    <Flex style={{ gap: '12px' }}>
                        <Button
                            type="button"
                            variant="link"
                            onClick={triggerBackgroundImageUpload}
                            disabled={isBackgroundUploading}
                        >
                            {isBackgroundUploading ? (
                                'Uploading...'
                            ) : (
                                <>
                                    <FiCamera /> Upload image
                                </>
                            )}
                        </Button>
                        <input
                            id="branding-background-input"
                            ref={uploadBackgroundInputRef}
                            onChange={event => uploadBackground(event.target.files[0])}
                            type="file"
                            accept=".png, .jpg, .jpeg"
                            hidden
                        />
                        {branding.background_image && (
                            <Button
                                disabled={isBackgroundRemoving}
                                type="button"
                                variant="link"
                                onClick={() => deleteBackground()}
                            >
                                <FiTrash2 /> Remove
                            </Button>
                        )}
                    </Flex>
                </Box>
            </Box>
            <Grid col={2} className="form-row-margin" style={{ marginBottom: 0 }}>
                <Box>
                    <Label htmlFor="branding-primary-color-input">Primary Color</Label>
                    <Box className="form-data">
                        <Flex style={{ gap: '16px' }}>
                            <Input
                                id="branding-primary-color-input"
                                type="text"
                                placeholder="#ffffff"
                                {...register('primary_color', {
                                    onChange: event => changeBranding('primary_color', event.target.value)
                                })}
                                style={{ width: '90px' }}
                                className={!isPrimaryColorValid && 'error'}
                            />
                            <ColorIndicator color={formState.primary_color} />
                        </Flex>
                        {!isPrimaryColorValid && <InputError>Invalid primary color</InputError>}
                    </Box>
                </Box>
                <Box>
                    <Label htmlFor="branding-secondary-color-input">Secondary Color</Label>
                    <Box className="form-data">
                        <Flex style={{ gap: '16px' }}>
                            <Input
                                id="branding-secondary-color-input"
                                type="text"
                                placeholder="#ffffff"
                                {...register('secondary_color', {
                                    onChange: event => changeBranding('secondary_color', event.target.value)
                                })}
                                style={{ width: '90px' }}
                                className={!isSecondaryColorValid && 'error'}
                            />
                            <ColorIndicator color={formState.secondary_color} />
                        </Flex>
                        {!isSecondaryColorValid && <InputError>Invalid secondary color</InputError>}
                    </Box>
                </Box>
            </Grid>
            <Box className="form-row-margin">
                <Label>Email Footer</Label>
                <Box className="form-data">
                    <SaveEditEditor
                        modules={EXTERNAL_RESTRICTIONS_QUILL_MODULES}
                        defaultValue={branding.email_footer}
                        onSave={html => changeBranding('email_footer', html)}
                    />
                </Box>
            </Box>
            <Box className="form-row-margin">
                <Label>Screener Footer</Label>
                <Box className="form-data">
                    <SaveEditEditor
                        modules={NO_VIDEO_QUILL_MODULES}
                        defaultValue={branding.form_footer}
                        onSave={html => changeBranding('form_footer', html)}
                    />
                </Box>
            </Box>

            {isCustomDomain && (
                <Grid col={2} className="form-row-margin" style={{ marginBottom: 0 }}>
                    <Box>
                        <Label htmlFor="branding-custom-domain-input">Custom Domain</Label>
                        <Box className="form-data">
                            <Input
                                id="branding-custom-domain-input"
                                type="text"
                                name="custom_domain"
                                placeholder="Custom Domain"
                                value={account.custom_domain}
                                disabled
                            />
                        </Box>
                    </Box>
                    <Box>
                        <Label htmlFor="branding-favicon-input">Favicon</Label>
                        <Box className="form-data">
                            {branding.favicon && (
                                <Box mb={2}>
                                    <img
                                        src={branding.favicon}
                                        style={{
                                            width: '32px',
                                            height: '32px',
                                            objectFit: 'contain',
                                            borderRadius: '4px',
                                            overflow: 'hidden'
                                        }}
                                        alt="Favicon"
                                    />
                                </Box>
                            )}

                            <Button
                                type="button"
                                variant="link"
                                onClick={triggerFaviconUpload}
                                disabled={isFaviconUploading}
                                mr={3}
                            >
                                {isFaviconUploading ? (
                                    'Uploading...'
                                ) : (
                                    <>
                                        <FiCamera /> Upload image
                                    </>
                                )}
                            </Button>
                            <input
                                id="branding-favicon-input"
                                ref={uploadFaviconInputRef}
                                type="file"
                                accept="image/x-icon, image/vnd.microsoft.icon"
                                hidden
                                onChange={event => {
                                    if (!isCustomDomain) return;

                                    return uploadFavicon(event.target.files[0]);
                                }}
                            />
                            {branding.favicon && (
                                <Button
                                    disabled={isFaviconRemoving}
                                    type="button"
                                    variant="link"
                                    onClick={() => deleteFavicon()}
                                >
                                    <FiTrash2 /> Remove
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Grid>
            )}
        </Box>
    );
}

/**
 * Branding form skeleton. Used to show a loading state while the branding form is being loaded.
 */
export function BrandingFormSkeleton() {
    return (
        <Box className="form-data">
            <Box>
                <Label>Branding Name (Internal)</Label>
                <Box className="form-data">
                    <Skeleton width="100%" height={32} />
                </Box>
            </Box>
            <Box className="form-row-margin">
                <Label>Logo</Label>
                <Box className="form-data">
                    <Skeleton width={48} height={32} />
                </Box>
            </Box>
            <Box className="form-row-margin">
                <Label>Screener Background</Label>
                <Box className="form-data">
                    <Skeleton width={48} height={32} />
                </Box>
            </Box>
            <Grid col={2} className="form-row-margin" style={{ marginBottom: 0 }}>
                <Box>
                    <Label>Primary Color</Label>
                    <Box className="form-data">
                        <Skeleton width={90} height={32} />
                    </Box>
                </Box>
                <Box>
                    <Label>Secondary Color</Label>
                    <Box className="form-data">
                        <Skeleton width={90} height={32} />
                    </Box>
                </Box>
            </Grid>
            <Box className="form-row-margin">
                <Label>Email Footer</Label>
                <Box className="form-data">
                    <Skeleton height={200} />
                </Box>
            </Box>
            <Box className="form-row-margin">
                <Label>Screener Footer</Label>
                <Box className="form-data">
                    <Skeleton height={200} />
                </Box>
            </Box>
        </Box>
    );
}
